import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormsService } from '@src/app/services/forms';
import { componentDestroyed, OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import includes from 'lodash/includes';
import { takeUntil, tap } from 'rxjs/operators';

import { FormEditorInputType } from '../_models';
import { IFormArrayTable } from './_models';

@Component({
	selector: 'app-form-array-table',
	templateUrl: './form-array-table.component.html',
	styleUrls: ['./form-array-table.component.scss']
})
export class FormArrayTableComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
	@Input() formData: IFormArrayTable;
	@Input() editable: boolean;
	@Input() formIsValid = false;
	@Input() titleTooltip: string;

	@Output() formIsValidChange = new EventEmitter<boolean>();

	public formEditorInputType = FormEditorInputType;
	public editing = false;
	public formInputs: FormGroup = new FormGroup({});
	public formInputsCreated = false;

	constructor(private formsService: FormsService) {
		super();
	}

	public ngOnInit(): void {
		let count = 1;
		this.formData.items.forEach((item: any) => {
			const name = 'item' + count;
			this.formInputs.addControl(name, new FormControl(item, this.formData.validators));
			this.formInputs.get(name).setValue(item);
			if (this.editable) {
				this.formInputs.get(name).enable();
			} else {
				this.formInputs.get(name).disable();
			}
			count++;
		});
	}

	public ngAfterViewInit(): void {
		this.formInputs.valueChanges
			.pipe(takeUntil(componentDestroyed(this)))
			.pipe(
				tap((_) => {
					this.formInputs.markAllAsTouched();
					this.changed();
				})
			)
			.subscribe(this.checkFormCompletion.bind(this));

		this.formInputs.statusChanges
			.pipe(takeUntil(componentDestroyed(this)))
			.pipe(
				tap((_) => {
					this.formInputs.markAllAsTouched();
				})
			)
			.subscribe(this.checkFormCompletion.bind(this));

		// Run validators
		setTimeout(() => this.formInputs.updateValueAndValidity());
	}

	public showDelete(): boolean {
		return !this.isRequired() || this.itemsLength() > 1;
	}

	public headerTitle(): string {
		return this.formData.title || '';
	}

	public deleteItem(index: number, item: any) {
		this.formData.items.splice(index, 1);
		this.formInputs.removeControl(`item${index + 1}`);

		let count = 1;
		for (const [key, control] of Object.entries(this.formInputs.controls)) {
			this.formInputs.removeControl(key);
			this.formInputs.addControl(`item${count}`, control);
			count++;
		}

		this.changed();
	}

	public addItem() {
		if (!this.formData.items) {
			this.formData.items = [];
		}
		let newItem;
		switch (this.formData.type) {
			case FormEditorInputType.number:
				newItem = 0;
				break;

			default:
				newItem = '';
				break;
		}
		this.formData.items.push(newItem);
		const name = 'item' + this.formData.items.length;
		this.formInputs.addControl(name, new FormControl(newItem, this.formData.validators));
		const control = this.formInputs.get(name);
		control.setValue(newItem);
		if (this.editable) {
			control.enable();
		} else {
			control.disable();
		}
		this.changed();
	}

	private isRequired(): boolean {
		return includes(this.formData.validators, Validators.required);
	}

	private itemsLength(): number {
		return this.formData && this.formData.items ? this.formData.items.length : 0;
	}

	private checkFormCompletion(x: any) {
		this.formIsValid = x === 'VALID';
		this.formIsValidChange.emit(this.formIsValid);
	}

	private changed() {
		if (this.formData.callback) {
			const forms: any = this.formInputs.value;
			const items: any[] = [];
			for (const key in forms) {
				if (Object.prototype.hasOwnProperty.call(forms, key)) {
					const value = forms[key];
					const control = this.formInputs.get(key);
					if (control.valid) {
						items.push(value);
					}
				}
			}
			this.formData.callback(items);
		}
	}
}
